import React, { useMemo } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { CheckboxField } from '@/components/formBuilder/formFields/CheckboxField';
import { KText } from '@/ui-library/components/text';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';

function findAllDescendants(data, id) {
  const descendants = [];
  const stack = [id];

  while (stack.length > 0) {
    const currentId = stack.pop();
    const children = data.filter((item) => item.parent_id === currentId);
    descendants.push(...children);
    stack.push(...children.map((child) => child.id));
  }

  return descendants;
}

function findAllAncestors(data, id) {
  const ancestors = [];
  let currentId = id;

  // Define a separate function to avoid capturing loop variables
  function findItemById(id) {
    return data.find((item) => item.id === id);
  }

  while (currentId) {
    const currentItem = findItemById(currentId);
    if (currentItem && currentItem.parent_id) {
      ancestors.push(currentItem);
      currentId = currentItem.parent_id;
    } else {
      break;
    }
  }

  return ancestors.slice(1); // Remove the starting item itself
}

const TreeItem = ({
  item,
  items,
  primaryParentStyles = {},
  childrenStyles = {},
  onSelect = () => {},
  onClose = () => {},
  currentSelectedValue,
  visibleItems,
  isCheckbox = true,
}) => {
  const children = items.filter(
    (child) => child.parent_id === item.id && visibleItems.has(child.id)
  );
  const isPrimaryParent = item.parent_id === null;
  const stylesToApply = isPrimaryParent ? primaryParentStyles : childrenStyles;

  const handleSelect = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onSelect(item);
    onClose?.();
  };

  const isSelected = currentSelectedValue.has(item.id);

  if (!visibleItems.has(item.id)) {
    return null;
  }

  return (
    <Box {...stylesToApply} onClick={handleSelect}>
      <Flex
        alignItems="center"
        p=".4em .5em"
        gap=".5em"
        cursor="pointer"
        _hover={{ bgColor: 'secondary-50' }}
        borderRadius="6px"
      >
        {isCheckbox && (
          <CheckboxField item={{}} field={{ value: isSelected }} />
        )}
        <KText
          size={isPrimaryParent ? 'sm-semi-bold' : 'sm-regular'}
          color={isSelected ? 'primary-500' : 'secondary-700'}
        >
          {item.name}
        </KText>
				{isSelected && <Icon as={CheckCircle} color="primary-500" />}
      </Flex>
      {children.map((child) => (
        <TreeItem
          key={child.id}
          item={child}
          items={items}
          onClose={onClose}
          currentSelectedValue={currentSelectedValue}
          primaryParentStyles={primaryParentStyles}
          childrenStyles={childrenStyles}
          onSelect={onSelect}
          visibleItems={visibleItems}
          isCheckbox={isCheckbox}
        />
      ))}
    </Box>
  );
};

const FilterNestedListWrapper = ({
  data,
  primaryParentStyles = {
    pr: '1.5em',
  },
  childrenStyles = {
    marginLeft: '10px',
    padding: '.25em .5em',
    borderLeft: '1px solid',
    borderColor: 'border-color',
    cursor: 'pointer',
  },
  onSelect = () => {},
  onClose,
  inputValue,
  currentSelectedValue,
  isCheckbox = true,
}) => {
  const visibleItems = useMemo(() => {
    const visible = new Set();

    if (inputValue?.length > 0) {
      data.forEach((item) => {
        // Check for direct match or if the item is an ancestor or descendant of a match
        const isMatch = item.name
          .toLowerCase()
          .includes(inputValue.toLowerCase());
        const hasMatchingAncestor = findAllAncestors(data, item.id).some(
          (ancestor) =>
            ancestor.name.toLowerCase().includes(inputValue.toLowerCase())
        );
        const hasMatchingDescendant = findAllDescendants(data, item.id).some(
          (descendant) =>
            descendant.name.toLowerCase().includes(inputValue.toLowerCase())
        );

        if (isMatch || hasMatchingAncestor || hasMatchingDescendant) {
          visible.add(item.id);
        }
      });
    } else {
      data.forEach((item) => visible.add(item.id));
    }

    return visible;
  }, [data, inputValue]);

  const rootItems = data.filter(
    (item) => item.parent_id === null && visibleItems.has(item.id)
  );

  if (rootItems.length === 0) {
    return (
      <Flex minH="5em" justifyContent="center" alignItems="center">
        <KText>No options found</KText>
      </Flex>
    );
  }

  return (
    <Box maxH="15em" overflowY="auto">
      {rootItems.map((item) => (
        <TreeItem
          key={item.id}
          item={item}
          items={data}
          onClose={onClose}
          currentSelectedValue={currentSelectedValue}
          primaryParentStyles={primaryParentStyles}
          childrenStyles={childrenStyles}
          onSelect={onSelect}
          visibleItems={visibleItems}
          isCheckbox={isCheckbox}
        />
      ))}
    </Box>
  );
};

export const FilterNestedList = (props) => {
  const {
    list: options = [],
    selected,
    onChange = () => {},
    searchTerm: inputValue,
    isCheckbox = true,
  } = props;

  const handleSelect = (selectedOption) => {
    onChange(selectedOption);
  };

  const currentSelectedValue = useMemo(() => {
    if (selected instanceof Set) {
      return selected;
    }

    if (Array.isArray(selected)) {
      return new Set(selected);
    }

    if (selected !== undefined && selected !== null) {
      return new Set([selected]);
    }

    return new Set();
  }, [selected]);

  return (
    <Box>
      <FilterNestedListWrapper
        currentSelectedValue={currentSelectedValue}
        data={options}
        onSelect={handleSelect}
        inputValue={inputValue}
        isCheckbox={isCheckbox}
      />
    </Box>
  );
};
